import type { KDFParams, DerivedKeys } from '../types';

/**
 * Generate a random salt for key derivation
 */
export function generateSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(16));
}

/**
 * Derive keys from master password using scrypt
 *
 * This implementation uses the Web Crypto API's PBKDF2 as a fallback
 * since scrypt is not natively available. For production, consider
 * using a WASM-based scrypt implementation.
 *
 * The derived key is split into:
 * - authKey (first 32 bytes): Used for authentication
 * - vaultKey (next 32 bytes): Used for encrypting vault data
 */
export async function deriveKeys(
  masterPassword: string,
  params: KDFParams
): Promise<DerivedKeys> {
  const encoder = new TextEncoder();
  const passwordBytes = encoder.encode(masterPassword);
  const saltBytes = base64ToBytes(params.salt);
  // Ensure salt is a proper Uint8Array for Web Crypto API
  const salt = new Uint8Array(saltBytes);

  // Import password as key material
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passwordBytes,
    'PBKDF2',
    false,
    ['deriveBits']
  );

  // Derive 64 bytes (512 bits) for both keys
  // Using PBKDF2 with high iterations as fallback for scrypt
  // In production, use scrypt via WASM for better security
  const iterations = params.N ? Math.max(params.N, 100000) : 310000;

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt,
      iterations,
      hash: 'SHA-256',
    },
    keyMaterial,
    512 // 64 bytes
  );

  const derivedBytes = new Uint8Array(derivedBits);

  return {
    authKey: derivedBytes.slice(0, 32),
    vaultKey: derivedBytes.slice(32, 64),
  };
}

/**
 * Generate default KDF parameters
 */
export function generateKDFParams(): KDFParams {
  return {
    algorithm: 'scrypt',
    salt: bytesToBase64(generateSalt()),
    N: 65536, // 2^16
    r: 8,
    p: 1,
  };
}

/**
 * Hash the auth key for sending to server
 */
export async function hashAuthKey(authKey: Uint8Array): Promise<string> {
  // Ensure we have a proper Uint8Array for Web Crypto API
  const keyBytes = new Uint8Array(authKey);
  const hashBuffer = await crypto.subtle.digest('SHA-256', keyBytes);
  return bytesToHex(new Uint8Array(hashBuffer));
}

/**
 * Convert bytes to base64 string
 */
export function bytesToBase64(bytes: Uint8Array): string {
  const binString = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join('');
  return btoa(binString);
}

/**
 * Convert base64 string to bytes
 */
export function base64ToBytes(base64: string): Uint8Array {
  const binString = atob(base64);
  return Uint8Array.from(binString, (char) => char.codePointAt(0) ?? 0);
}

/**
 * Convert bytes to hex string
 */
export function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Convert hex string to bytes
 */
export function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return bytes;
}

